/**
 * SLA clock domain logic.
 *
 * Two clocks per SLA ticket: response and solution. Both are "wall time"
 * deadlines adjusted by a paused-duration accumulator:
 *
 *   effective_deadline = original_deadline + total_paused_duration
 *
 * Pause triggers (SPEC.md §7.3):
 *   - Ticket status = awaiting_client
 *   - Outside business hours (default Mon–Fri 09:00–17:00 Australia/Sydney,
 *     overridable per contract — e.g. VRC includes Saturday)
 *   - Manual pause (rare, ops-initiated)
 *
 * Pure functions only — no IO, no Date.now() reads. Time is always passed
 * in. This is testable, deterministic, and ports to any runtime.
 */

import type { SlaContract } from '../types/contract.js';
import type { Ticket } from '../types/ticket.js';

/**
 * Visual state used by the SLA timer pill (design handoff Decisions #6).
 * Maps directly to `<SLA state label>` in the design primitives.
 */
export type SlaState =
  | 'met'
  | 'ok'
  | 'warn'
  | 'breaching'
  | 'breached'
  | 'paused';

export interface SlaSnapshot {
  state: SlaState;
  /** Milliseconds until the effective deadline. Negative if breached. */
  millisToDeadline: number;
  /** Effective deadline after applying pause accumulator. */
  effectiveDeadlineAt: Date;
  /** Human-readable label for the pill — "12m to solution", "Met", etc. */
  label: string;
}

export interface BusinessHours {
  /** 0 = Sunday, 6 = Saturday. */
  daysOfWeek: ReadonlyArray<number>;
  /** Local hour (0–23) — start of the working window. */
  startHour: number;
  /** Local hour (0–23) — end of the working window (exclusive). */
  endHour: number;
  /** IANA timezone — e.g. "Australia/Sydney". */
  timezone: string;
}

/** Default business hours per SPEC.md §5 row 6a. */
export const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  daysOfWeek: [1, 2, 3, 4, 5], // Mon–Fri
  startHour: 9,
  endHour: 17,
  timezone: 'Australia/Sydney',
};

/**
 * Module-level cache of `Intl.DateTimeFormat` instances keyed by
 * timezone. Constructing one is ~1 µs and `formatToParts` is ~0.5 µs;
 * before this cache, the minute-walk in `businessHoursElapsedMs` would
 * allocate a fresh formatter on every iteration (525_600 per
 * year-old ticket). On a 20-row inbox with per-second SLA ticking
 * that saturated the main thread and made every interaction sluggish.
 */
const FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>();
function getBusinessHoursFormatter(timezone: string): Intl.DateTimeFormat {
  let fmt = FORMATTER_CACHE.get(timezone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      hour12: false,
    });
    FORMATTER_CACHE.set(timezone, fmt);
  }
  return fmt;
}

/**
 * Determines whether a given instant falls within the configured
 * business hours. Uses Intl APIs to compute the local hour + day in
 * the contract's timezone — no dependency on host system TZ.
 */
export function isInsideBusinessHours(
  at: Date,
  hours: BusinessHours
): boolean {
  // Format the instant in the contract's timezone, then parse the parts.
  const fmt = getBusinessHoursFormatter(hours.timezone);
  const parts = fmt.formatToParts(at);
  const weekdayLabel = parts.find((p) => p.type === 'weekday')?.value ?? '';
  const hourStr = parts.find((p) => p.type === 'hour')?.value ?? '0';
  const hour = Number.parseInt(hourStr, 10);

  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  const dayOfWeek = weekdayMap[weekdayLabel];
  if (dayOfWeek === undefined) return false;

  if (!hours.daysOfWeek.includes(dayOfWeek)) return false;
  return hour >= hours.startHour && hour < hours.endHour;
}

/**
 * Initial deadlines for a fresh SLA ticket — wall-clock offsets from
 * createdAt by the contract's response/solution targets.
 *
 * Per SPEC §7.3 the *stored* deadline is plain wall-clock; the
 * out-of-business-hours and awaiting-client pause-time are applied
 * at READ time by `computeSolutionSnapshot` (which adds the
 * accumulator and the on-the-fly out-of-hours offset). So at
 * creation we just stamp `createdAt + N minutes`.
 *
 * BAU and Internal contracts have no hard SLA — callers should not
 * invoke this for them; their deadlines stay null.
 */
export function computeInitialDeadlines(
  contract: SlaContract,
  createdAt: Date
): {
  responseDeadlineAt: string;
  solutionDeadlineAt: string;
} {
  const baseMs = createdAt.getTime();
  return {
    responseDeadlineAt: new Date(
      baseMs + contract.responseTargetMinutes * 60_000
    ).toISOString(),
    solutionDeadlineAt: new Date(
      baseMs + contract.solutionTargetMinutes * 60_000
    ).toISOString(),
  };
}

/**
 * Computes the current SLA snapshot for a ticket against its solution
 * deadline. Used in the side panel and triage list.
 *
 * Warn threshold: <25% of total target window remaining.
 * Breaching threshold: <10% remaining.
 */
export function computeSolutionSnapshot(
  ticket: Ticket,
  contract: SlaContract,
  now: Date,
  businessHours: BusinessHours = DEFAULT_BUSINESS_HOURS
): SlaSnapshot | null {
  if (ticket.status === 'resolved' || ticket.status === 'closed') {
    return ticket.solutionDeadlineAt
      ? {
          state: 'met',
          millisToDeadline: 0,
          effectiveDeadlineAt: new Date(ticket.solutionDeadlineAt),
          label: 'Met',
        }
      : null;
  }

  if (!ticket.solutionDeadlineAt) return null;

  const isPaused =
    ticket.status === 'awaiting_client' ||
    ticket.pausedAt !== null ||
    (contract.businessHoursOnly &&
      !isInsideBusinessHours(now, businessHours));

  // Effective deadline = original + status-pause accumulator + the
  // total time spent outside business hours since the ticket was
  // created (only for business-hours-only contracts). Out-of-hours
  // time is computed on the fly rather than persisted, so the column
  // stays the source of truth for status-driven pauses only.
  let extraOffsetMs = ticket.solutionPausedMs;
  if (contract.businessHoursOnly) {
    extraOffsetMs += nonBusinessHoursElapsedMs(
      new Date(ticket.createdAt),
      now,
      businessHours
    );
  }
  const effective = new Date(
    new Date(ticket.solutionDeadlineAt).getTime() + extraOffsetMs
  );

  if (isPaused) {
    return {
      state: 'paused',
      millisToDeadline: effective.getTime() - now.getTime(),
      effectiveDeadlineAt: effective,
      label: pausedLabel(now, businessHours),
    };
  }

  const millisToDeadline = effective.getTime() - now.getTime();

  if (millisToDeadline < 0) {
    return {
      state: 'breached',
      millisToDeadline,
      effectiveDeadlineAt: effective,
      label: humanizeMillis(-millisToDeadline) + ' overdue',
    };
  }

  const totalTargetMs = contract.solutionTargetMinutes * 60_000;
  const remainingFrac = millisToDeadline / totalTargetMs;

  let state: SlaState;
  if (remainingFrac < 0.1) state = 'breaching';
  else if (remainingFrac < 0.25) state = 'warn';
  else state = 'ok';

  return {
    state,
    millisToDeadline,
    effectiveDeadlineAt: effective,
    label: humanizeMillis(millisToDeadline) + ' to solution',
  };
}

// ── Helpers for O(1) business-hours calculation ──────────────────

interface LocalParts {
  year: number;
  month: number;
  day: number;
  weekday: number;
  hour: number;
  minute: number;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

const PARTS_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>();
function getPartsFormatter(timezone: string): Intl.DateTimeFormat {
  let fmt = PARTS_FORMATTER_CACHE.get(timezone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    PARTS_FORMATTER_CACHE.set(timezone, fmt);
  }
  return fmt;
}

function getLocalParts(date: Date, timezone: string): LocalParts {
  const parts = getPartsFormatter(timezone).formatToParts(date);
  const get = (t: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === t)?.value ?? '0';
  return {
    year: Number.parseInt(get('year'), 10),
    month: Number.parseInt(get('month'), 10),
    day: Number.parseInt(get('day'), 10),
    weekday: WEEKDAY_INDEX[get('weekday')] ?? 0,
    hour: Number.parseInt(get('hour'), 10),
    minute: Number.parseInt(get('minute'), 10),
  };
}

function calendarDaysBetween(a: LocalParts, b: LocalParts): number {
  const aMs = Date.UTC(a.year, a.month - 1, a.day);
  const bMs = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((bMs - aMs) / 86_400_000);
}

/**
 * Sum of business-hours-elapsed time between two instants (ms).
 *
 * Uses an O(1) mathematical approach: extracts local time parts at
 * `from` and `to` (2 cached Intl calls), then computes via modular
 * weekday arithmetic — partial-first-day + full-weeks +
 * remainder-days + partial-last-day.
 *
 * Replaces the previous minute-walk (up to 525 600 iterations) which
 * caused a 1.9 s Long Task on 20-row SLA inboxes.
 *
 * Precision: minute-level (same as the old step-by-60 000 ms loop).
 */
export function businessHoursElapsedMs(
  from: Date,
  to: Date,
  hours: BusinessHours = DEFAULT_BUSINESS_HOURS
): number {
  if (to.getTime() <= from.getTime()) return 0;

  const fromL = getLocalParts(from, hours.timezone);
  const toL = getLocalParts(to, hours.timezone);

  const bizStart = hours.startHour * 60;
  const bizEnd = hours.endHour * 60;
  const bizPerDay = bizEnd - bizStart;

  const isBizDay = (wd: number): boolean => hours.daysOfWeek.includes(wd);

  /** Business minutes in [lo, hi] ∩ [bizStart, bizEnd]. */
  const overlap = (lo: number, hi: number): number =>
    Math.max(0, Math.min(hi, bizEnd) - Math.max(lo, bizStart));

  const totalDays = calendarDaysBetween(fromL, toL);

  // Same calendar day
  if (totalDays === 0) {
    if (!isBizDay(fromL.weekday)) return 0;
    return (
      overlap(
        fromL.hour * 60 + fromL.minute,
        toL.hour * 60 + toL.minute
      ) * 60_000
    );
  }

  let bizMin = 0;

  // Partial first day: cursor → end of business
  if (isBizDay(fromL.weekday)) {
    bizMin += overlap(fromL.hour * 60 + fromL.minute, bizEnd);
  }

  // Partial last day: start of business → cursor
  if (isBizDay(toL.weekday)) {
    bizMin += overlap(bizStart, toL.hour * 60 + toL.minute);
  }

  // Full days in between
  const fullDays = totalDays - 1;
  if (fullDays > 0) {
    const fullWeeks = Math.floor(fullDays / 7);
    const remainder = fullDays % 7;
    bizMin += fullWeeks * hours.daysOfWeek.length * bizPerDay;
    for (let i = 0; i < remainder; i++) {
      if (isBizDay((fromL.weekday + 1 + i) % 7)) {
        bizMin += bizPerDay;
      }
    }
  }

  return bizMin * 60_000;
}

/** Complement of {@link businessHoursElapsedMs} — total time spent
 *  outside business hours between two instants. Used by the breach
 *  detector to extend the effective deadline by however many ms were
 *  spent off-hours. */
export function nonBusinessHoursElapsedMs(
  from: Date,
  to: Date,
  hours: BusinessHours = DEFAULT_BUSINESS_HOURS
): number {
  const fromMs = from.getTime();
  const toMs = to.getTime();
  if (toMs <= fromMs) return 0;
  return toMs - fromMs - businessHoursElapsedMs(from, to, hours);
}

/**
 * Pure helper computing the pause-field deltas when a ticket's status
 * changes. SPEC §7.3:
 *
 *   - `awaiting_client` pauses both clocks. `paused_at` stamps the
 *     instant the pause began.
 *   - Any other status runs clocks. When transitioning OUT of
 *     `awaiting_client`, accrue (now − paused_at) into BOTH
 *     response_paused_ms and solution_paused_ms and clear paused_at.
 *
 * Both fields receive the accrued duration; the breach detector reads
 * each as needed (response math ignores the field once first_response_at
 * is set, so the duplication is cheap and removes a branching ambiguity).
 *
 * Returns `null` when no pause-field write is required — caller can
 * skip the column in the UPDATE.
 */
export interface PauseFields {
  pausedAt: string | null;
  responsePausedMs: number;
  solutionPausedMs: number;
}

export interface ApplyPauseInput {
  oldStatus: string;
  newStatus: string;
  current: PauseFields;
  now: Date;
}

export function applyPauseTransition(
  input: ApplyPauseInput
): PauseFields | null {
  const { oldStatus, newStatus, current, now } = input;

  // Entering awaiting_client from a running state — start a pause.
  if (newStatus === 'awaiting_client' && oldStatus !== 'awaiting_client') {
    if (current.pausedAt) return null; // already paused; idempotent
    return {
      pausedAt: now.toISOString(),
      responsePausedMs: current.responsePausedMs,
      solutionPausedMs: current.solutionPausedMs,
    };
  }

  // Leaving awaiting_client — accrue elapsed pause and clear stamp.
  if (oldStatus === 'awaiting_client' && newStatus !== 'awaiting_client') {
    if (!current.pausedAt) {
      // Stamp missing; nothing to accrue. Defensive — shouldn't happen
      // after a clean install but leaves prod resilient to legacy rows.
      return null;
    }
    const startedMs = Date.parse(current.pausedAt);
    if (Number.isNaN(startedMs)) return null;
    const elapsed = Math.max(0, now.getTime() - startedMs);
    return {
      pausedAt: null,
      responsePausedMs: current.responsePausedMs + elapsed,
      solutionPausedMs: current.solutionPausedMs + elapsed,
    };
  }

  return null;
}

/** "12m to solution", "3h 22m to response", "1d 4h to solution" */
export function humanizeMillis(ms: number): string {
  const totalMin = Math.max(0, Math.floor(ms / 60_000));
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const minutes = totalMin % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function pausedLabel(now: Date, hours: BusinessHours): string {
  if (isInsideBusinessHours(now, hours)) {
    return 'Paused — awaiting client';
  }
  return 'Paused — outside business hours';
}
